import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    CssBaseline,
    ThemeProvider,
    Typography,
    Grid,
    Card,
    CardContent,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    CircularProgress,
    Skeleton
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { getAllFAQs, postFAQ, updateFAQ, deleteFAQ } from "../../context/FAQs";
import darkTheme from "./dashboardTheme";
import ConfirmDialog from "./ConfirmDialog";

const FaqsComponent = () => {
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [open, setOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [form, setForm] = useState({ question: '', response: '', language: 'en' });
    const [selectedId, setSelectedId] = useState(null);
    const [filterLang, setFilterLang] = useState('all');

    const fetchFaqs = async () => {
        try {
            setLoading(true);
            const res = await getAllFAQs();
            setFaqs(res.data);
        } catch (error) {
            console.error("Error fetching FAQs:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFaqs();
    }, []);

    const handleSave = async () => {
        try {
            setSaving(true);
            if (editMode && selectedId) {
                await updateFAQ(selectedId, form);
            } else {
                await postFAQ(form.language, form.question, form.response);
            }
            await fetchFaqs();
            setOpen(false);
            resetForm();
        } catch (error) {
            console.error("Save error:", error);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (faq) => {
        setEditMode(true);
        setForm({ question: faq.question, response: faq.response, language: faq.language });
        setSelectedId(faq._id);
        setOpen(true);
    };

    // FAQ waiting for delete confirmation, and why the last attempt failed.
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleteError, setDeleteError] = useState('');

    const closeDeleteDialog = () => {
        setPendingDelete(null);
        setDeleteError('');
    };

    const handleDelete = async () => {
        const id = pendingDelete._id;
        try {
            setDeleting(id);
            setDeleteError('');
            await deleteFAQ(id);
            await fetchFaqs();
            closeDeleteDialog();
        } catch (error) {
            setDeleteError(error.message || 'Could not delete this FAQ.');
        } finally {
            setDeleting(false);
        }
    };

    const resetForm = () => {
        setForm({ question: '', response: '', language: 'en' });
        setEditMode(false);
        setSelectedId(null);
    };

    const filteredFaqs = filterLang === 'all' ? faqs : faqs.filter(faq => faq.language === filterLang);

    return (
        <ThemeProvider theme={darkTheme}>
            <CssBaseline/>
            <Box sx={{ minHeight: "100vh", py: 4, backgroundColor: "background.default" }}>
                <Container maxWidth="md">
                    <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>FAQs</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Questions and answers shown in the app's help section.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                        <FormControl sx={{ minWidth: 150 }}>
                            <InputLabel>Filter by Language</InputLabel>
                            <Select
                                value={filterLang}
                                onChange={(e) => setFilterLang(e.target.value)}
                                sx={{ color: 'white' }}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="en">English</MenuItem>
                                <MenuItem value="ar">Arabic</MenuItem>
                                <MenuItem value="fr">French</MenuItem>
                                <MenuItem value="es">Spanish</MenuItem>
                                <MenuItem value="ur">Urdu</MenuItem>
                            </Select>
                        </FormControl>

                        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>
                            Add FAQ
                        </Button>
                    </Box>

                    {loading ? (
                        <Grid container spacing={3}>
                            {[...Array(3)].map((_, index) => (
                                <Grid item xs={12} key={index}>
                                    <Card>
                                        <CardContent>
                                            <Skeleton variant="text" width="60%" height={40} />
                                            <Skeleton variant="text" width="100%" height={80} />
                                            <Skeleton variant="text" width="30%" height={20} />
                                            <Box mt={1} display="flex">
                                                <Skeleton variant="circular" width={40} height={40} />
                                                <Skeleton variant="circular" width={40} height={40} sx={{ ml: 1 }} />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Grid container spacing={3}>
                            {filteredFaqs.length > 0 ? (
                                filteredFaqs.map((faq) => (
                                    <Grid item xs={12} key={faq._id}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6">{faq.question}</Typography>
                                                <Typography variant="body1">{faq.response}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Language: {faq.language.toUpperCase()}
                                                </Typography>
                                                <Box mt={1}>
                                                    <IconButton onClick={() => handleEdit(faq)}>
                                                        <Edit />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => setPendingDelete(faq)}
                                                        aria-label="Delete FAQ"
                                                        disabled={deleting === faq._id}
                                                    >
                                                        {deleting === faq._id ? (
                                                            <CircularProgress size={24} />
                                                        ) : (
                                                            <Delete />
                                                        )}
                                                    </IconButton>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))
                            ) : (
                                <Grid item xs={12}>
                                    <Typography variant="body1" textAlign="center">
                                        No FAQs found. Add one to get started!
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}

                    <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
                        <DialogTitle>{editMode ? "Edit FAQ" : "Add FAQ"}</DialogTitle>
                        <DialogContent>
                            <TextField
                                label="Question"
                                fullWidth
                                margin="normal"
                                value={form.question}
                                onChange={(e) => setForm({ ...form, question: e.target.value })}
                            />
                            <TextField
                                label="Response"
                                fullWidth
                                margin="normal"
                                multiline
                                rows={4}
                                value={form.response}
                                onChange={(e) => setForm({ ...form, response: e.target.value })}
                            />
                            <FormControl fullWidth margin="normal">
                                <InputLabel sx={{ color: 'white' }}>Language</InputLabel>
                                <Select
                                    value={form.language}
                                    onChange={(e) => setForm({ ...form, language: e.target.value })}
                                    sx={{ color: 'white' }}
                                >
                                    <MenuItem value="en">English</MenuItem>
                                    <MenuItem value="ar">Arabic</MenuItem>
                                    <MenuItem value="fr">French</MenuItem>
                                    <MenuItem value="es">Spanish</MenuItem>
                                    <MenuItem value="ur">Urdu</MenuItem>
                                </Select>
                            </FormControl>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => { setOpen(false); resetForm(); }}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={saving || !form.question || !form.response}
                                startIcon={saving ? <CircularProgress size={20} /> : null}
                            >
                                {editMode ? (saving ? "Updating..." : "Update") : (saving ? "Creating..." : "Create")}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Container>
            </Box>
            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title="Delete this FAQ?"
                message={pendingDelete ? `"${pendingDelete.question}" will be removed from the app.` : ''}
                loading={Boolean(deleting)}
                error={deleteError}
                onConfirm={handleDelete}
                onClose={closeDeleteDialog}
            />
        </ThemeProvider>
    );
};

export default FaqsComponent;